import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Map, 
  Book, 
  MessageSquare, 
  TrendingUp,
  Cpu,
  Database,
  Target,
  CheckCircle,
  ArrowRight,
  Star,
  Users,
  Zap,
  Shield,
  Clock,
  Award,
  Menu,
  X,
  Home,
  Activity,
  DollarSign,
  LogIn,
  UserPlus
} from 'react-feather';
import { loadStripe } from '@stripe/stripe-js';
import './Landing.css';
import DashboardPreview from './DashboardPreview';
import CookieBanner from './CookieBanner';
import { subscriptionService } from '../services/api';
import { getRecentPosts } from '../data/blogData';

// Import landing images
import homeImage from '../assets/landing/home.png';
import goalsImage from '../assets/landing/goals.png';
import roadmapImage from '../assets/landing/roadmap.png';
import lessonImage from '../assets/landing/lesson.png';

const Landing = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [activeTab, setActiveTab] = useState(0); // For How It Works tabs
  const [isVisible, setIsVisible] = useState({});
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [recentBlogPosts] = useState(getRecentPosts(3));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('.section-animate');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  // Auto-progress through steps
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Close mobile menu when clicking on links
  const handleNavLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const nav = document.querySelector('.landing-nav');
      if (nav && !nav.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Handle Pro plan upgrade
  const handleUpgradeToPro = async () => {
    setIsUpgrading(true);
    try {
      // Check if user is logged in
      const token = localStorage.getItem('token');
      if (!token) {
        // Redirect to register/login with pro plan intent
        window.location.href = '/register?plan=pro';
        return;
      }

      // User is logged in, create checkout session
      const { sessionId, publicKey } = await subscriptionService.createCheckoutSession();
      
      // Initialize Stripe with the fetched public key
      const stripe = await loadStripe(publicKey);
      if (!stripe) {
        throw new Error("Stripe.js failed to load.");
      }

      // Redirect to Stripe Checkout
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        console.error("Stripe checkout error:", error);
        alert('Failed to redirect to checkout. Please try again.');
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      // If there's an error and user isn't logged in, redirect to register
      if (error.response?.status === 401) {
        window.location.href = '/register?plan=pro';
      } else {
        alert('Failed to initiate upgrade. Please try again.');
      }
    } finally {
      setIsUpgrading(false);
    }
  };

  return (
    <div className="landing-container">
      {/* Navigation Bar */}
      <nav className="landing-nav">
        <div className="nav-left">
          <div className="logo">
            <span>Brainy</span>
          </div>
          
          {/* Desktop Navigation Links */}
          <div className="nav-links desktop-nav">
            <a href="#hero" className="nav-link">Home</a>
            <a href="#problem" className="nav-link">Why Us</a>
            <a href="#how-it-works" className="nav-link">How It Works</a>
            <a href="#blog" className="nav-link">Blog</a>
            <a href="#pricing" className="nav-link">Pricing</a>
          </div>
        </div>

        {/* Desktop Navigation Right */}
        <div className="nav-right desktop-nav">
          <button onClick={() => window.location.href = '/login'} className="nav-login-btn">Log in</button>
          <button onClick={() => window.location.href = '/register'} className="nav-signup-btn">Sign up</button>
        </div>

        {/* Mobile Menu Toggle Button */}
        <button 
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Mobile Navigation Menu */}
        <div className={`mobile-nav ${isMobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-nav-content">
            {/* Include nav-left content in mobile menu */}
            <div className="mobile-nav-header">
              <div className="mobile-nav-logo">
                <span>Brainy</span>
              </div>
            </div>
            
            <div className="mobile-nav-links">
              <a href="#hero" className="mobile-nav-link" onClick={handleNavLinkClick}>
                <Home className="mobile-nav-icon" size={20} />
                <span className="mobile-nav-label">Home</span>
              </a>
              <a href="#problem" className="mobile-nav-link" onClick={handleNavLinkClick}>
                <Award className="mobile-nav-icon" size={20} />
                <span className="mobile-nav-label">Why Us</span>
              </a>
              <a href="#how-it-works" className="mobile-nav-link" onClick={handleNavLinkClick}>
                <Activity className="mobile-nav-icon" size={20} />
                <span className="mobile-nav-label">How It Works</span>
              </a>
              <a href="#blog" className="mobile-nav-link" onClick={handleNavLinkClick}>
                <Book className="mobile-nav-icon" size={20} />
                <span className="mobile-nav-label">Blog</span>
              </a>
              <a href="#pricing" className="mobile-nav-link" onClick={handleNavLinkClick}>
                <DollarSign className="mobile-nav-icon" size={20} />
                <span className="mobile-nav-label">Pricing</span>
              </a>
            </div>
            
            <div className="mobile-nav-buttons">
              <button 
                onClick={() => {
                  window.location.href = '/login';
                  handleNavLinkClick();
                }} 
                className="mobile-nav-login-btn"
              >
                <LogIn size={18} />
                Log in
              </button>
              <button 
                onClick={() => {
                  window.location.href = '/register';
                  handleNavLinkClick();
                }} 
                className="mobile-nav-signup-btn"
              >
                <UserPlus size={18} />
                Sign up
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && <div className="mobile-menu-overlay" onClick={handleNavLinkClick}></div>}
      </nav>

      {/* New Feature Badge */}
      <div className="new-feature-badge" onClick={() => window.location.href = '/register'}>
        <span className="new-badge">NEW</span>
        <span className="feature-text">AI-Powered Learning Paths Just Released!</span>
        <span className="badge-arrow">→</span>
      </div>

      {/* 1. Hero Section */}
      <section id="hero" className="hero-section">
        <h1 className="hero-title">
        <span className="title-highlight">Brainy</span> - Your AI-Powered Studying Assistant
        </h1>
        <p className="hero-description">
            All that you need for studying - roadmaps, lessons, assessments, notes - powered by AI.
        </p>

        {/* Call to Action Buttons */}
        <div className="cta-buttons">
          <button onClick={() => window.location.href = '/register'} className="cta-signup-btn">Start For Free</button>
          <button onClick={() => window.location.href = '#how-it-works'} className="cta-learn-more-btn">Learn More</button>
        </div>

        {/* Hero image */}
        <div className="hero-graphic">
          <img src={homeImage} alt="Brainy Dashboard" className="hero-image" />
        </div>
      </section>

      {/* Enhanced How It Works Section */}
      <section id="how-it-works" className={`how-it-works-section section-animate ${isVisible['how-it-works'] ? 'visible' : ''}`}>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">How Brainy Works</h2>
            <p className="section-subtitle">Four simple steps to transform your learning experience</p>
          </div>
          
          <div className="how-it-works-tabs-container">
            {/* Tab Navigation */}
            <div className="tabs-list">
              {[
                {
                  title: "Set Your Goals",
                  icon: Target,
                  image: goalsImage
                },
                {
                  title: "Get Your Roadmap",
                  icon: Map,
                  image: roadmapImage
                },
                {
                  title: "Learn Interactively",
                  icon: Book,
                  image: lessonImage
                },
                {
                  title: "Track Progress",
                  icon: TrendingUp,
                  image: homeImage
                }
              ].map((tab, index) => (
                <div 
                  key={index}
                  className={`tab-item ${activeTab === index ? 'active' : ''}`}
                  onClick={() => setActiveTab(index)}
                >
                  <div className="tab-icon">
                    <tab.icon size={20} />
                  </div>
                  <div className="tab-content">
                    <h3>{tab.title}</h3>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Tab Image Display */}
            <div className="tab-image-container">
              <img 
                src={[goalsImage, roadmapImage, lessonImage, homeImage][activeTab]} 
                alt={["Goals Dashboard", "Roadmap View", "Interactive Lesson", "Progress Dashboard"][activeTab]}
                className="tab-display-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Problem Statement Section */}
      <section id="problem" className={`problem-section section-animate ${isVisible.problem ? 'visible' : ''}`}>
        <div className="section-container">
          <div className="problem-content">
            <div className="problem-left">
              <div className="problem-badge">The Challenge</div>
              <h2 className="problem-title">Traditional Learning is Broken</h2>
              <div className="problem-items">
                <div className="problem-item">
                  <div className="problem-icon">
                    <Clock size={24} />
                  </div>
                  <div className="problem-text">
                    <h4>One-Size-Fits-All Approach</h4>
                    <p>Generic courses that don't adapt to your unique learning style or pace</p>
                  </div>
                </div>
                <div className="problem-item">
                  <div className="problem-icon">
                    <Users size={24} />
                  </div>
                  <div className="problem-text">
                    <h4>Lack of Personalization</h4>
                    <p>No consideration for your background, goals, or preferred learning methods</p>
                  </div>
                </div>
                <div className="problem-item">
                  <div className="problem-icon">
                    <TrendingUp size={24} />
                  </div>
                  <div className="problem-text">
                    <h4>No Progress Tracking</h4>
                    <p>Difficulty measuring real progress and identifying knowledge gaps</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="problem-right">
              <div className="problem-visual">
                <div className="comparison-container">
                  {/* Traditional Learning Side */}
                  <div className="comparison-side traditional">
                    <div className="comparison-header">
                      <div className="comparison-icon traditional-icon">
                        <Clock size={24} />
                      </div>
                      <h4>Traditional Learning</h4>
                    </div>
                    <div className="learning-flow">
                      <div className="flow-step static">
                        <div className="step-icon">
                          <Users size={16} />
                        </div>
                        <span>Same for Everyone</span>
                      </div>
                      <div className="flow-connector static"></div>
                      <div className="flow-step static">
                        <div className="step-icon">
                          <Book size={16} />
                        </div>
                        <span>Generic Content</span>
                      </div>
                      <div className="flow-connector static"></div>
                      <div className="flow-step static">
                        <div className="step-icon">
                          <X size={16} />
                        </div>
                        <span>One Size Fits All</span>
                      </div>
                    </div>
                    <div className="outcome-badge negative">
                      Limited Results
                    </div>
                  </div>

                  {/* VS Divider */}
                  <div className="vs-divider-modern">
                    <div className="vs-circle">
                      <span>VS</span>
                    </div>
                  </div>

                  {/* AI Learning Side */}
                  <div className="comparison-side ai-powered">
                    <div className="comparison-header">
                      <div className="comparison-icon ai-icon">
                        <Cpu size={24} />
                      </div>
                      <h4>AI-Powered Learning</h4>
                    </div>
                    <div className="learning-flow">
                      <div className="flow-step dynamic">
                        <div className="step-icon">
                          <Target size={16} />
                        </div>
                        <span>Personal Goals</span>
                      </div>
                      <div className="flow-connector dynamic"></div>
                      <div className="flow-step dynamic">
                        <div className="step-icon">
                          <Zap size={16} />
                        </div>
                        <span>Adaptive Content</span>
                      </div>
                      <div className="flow-connector dynamic"></div>
                      <div className="flow-step dynamic">
                        <div className="step-icon">
                          <TrendingUp size={16} />
                        </div>
                        <span>Smart Progress</span>
                      </div>
                    </div>
                    <div className="outcome-badge positive">
                      <Star size={16} />
                      Optimal Results
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Features Section */}
      <section id="features" className={`features-section section-animate ${isVisible.features ? 'visible' : ''}`}>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Powerful Features That Transform Learning</h2>
            <p className="section-subtitle">Experience the future of personalized education with our AI-driven platform</p>
          </div>
          
          <div className="features-grid-modern">
            <div className="feature-card-modern" data-feature="roadmap">
              <div className="feature-card-inner">
                <div className="feature-icon-container">
                  <div className="feature-icon-bg"></div>
                  <Map className="feature-icon" size={32} />
                </div>
                <div className="feature-content">
                  <h3>AI-Powered Roadmap</h3>
                  <p>Get a personalized learning journey tailored to your goals, skill level, and learning preferences</p>
                  <div className="feature-stats">
                    <div className="stat">
                      <Star size={16} />
                      <span>4.9/5 Rating</span>
                    </div>
                  </div>
                </div>
                <div className="feature-hover-overlay">
                  <ArrowRight size={24} />
                </div>
              </div>
            </div>
            
            <div className="feature-card-modern" data-feature="lessons">
              <div className="feature-card-inner">
                <div className="feature-icon-container">
                  <div className="feature-icon-bg"></div>
                  <Book className="feature-icon" size={32} />
                </div>
                <div className="feature-content">
                  <h3>Interactive Lessons</h3>
                  <p>Engage with dynamic, AI-generated content that adapts to your understanding in real-time</p>
                  <div className="feature-stats">
                    <div className="stat">
                      <Users size={16} />
                      <span>1K+ Active Learners</span>
                    </div>
                  </div>
                </div>
                <div className="feature-hover-overlay">
                  <ArrowRight size={24} />
                </div>
              </div>
            </div>
            
            <div className="feature-card-modern" data-feature="chat">
              <div className="feature-card-inner">
                <div className="feature-icon-container">
                  <div className="feature-icon-bg"></div>
                  <MessageSquare className="feature-icon" size={32} />
                </div>
                <div className="feature-content">
                  <h3>Smart AI Assistant</h3>
                  <p>Get instant help and explanations from our advanced AI that understands your learning context</p>
                  <div className="feature-stats">
                    <div className="stat">
                      <Zap size={16} />
                      <span>Instant Responses</span>
                    </div>
                  </div>
                </div>
                <div className="feature-hover-overlay">
                  <ArrowRight size={24} />
                </div>
              </div>
            </div>
            
            <div className="feature-card-modern" data-feature="progress">
              <div className="feature-card-inner">
                <div className="feature-icon-container">
                  <div className="feature-icon-bg"></div>
                  <TrendingUp className="feature-icon" size={32} />
                </div>
                <div className="feature-content">
                  <h3>Advanced Analytics</h3>
                  <p>Track your progress with detailed insights and recommendations for optimal learning</p>
                  <div className="feature-stats">
                    <div className="stat">
                      <Award size={16} />
                      <span>Achievement System</span>
                    </div>
                  </div>
                </div>
                <div className="feature-hover-overlay">
                  <ArrowRight size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section id="blog" className={`blog-section section-animate ${isVisible.blog ? 'visible' : ''}`}>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Latest from Our Blog</h2>
            <p className="section-subtitle">Insights, tips, and updates on AI-powered learning</p>
          </div>
          
          <div className="blog-grid-modern">
            {recentBlogPosts.map((post) => (
              <article 
                key={post.id} 
                className={`blog-card-modern ${post.type === 'news' ? 'blog-card-compact' : ''}`}
                onClick={() => window.location.href = `/blog/${post.id}`}
              >
                {post.type === 'article' && (
                  <>
                    <div className="blog-image-container">
                      <img src={post.image} alt={post.title} className="blog-image" />
                      <div className="blog-category-badge">{post.category}</div>
                    </div>
                    <div className="blog-content">
                      <div className="blog-meta">
                        <span className="blog-date">{post.formattedDate}</span>
                        <span className="blog-read-time">{post.readTime} min read</span>
                      </div>
                      <h3 className="blog-title">{post.title}</h3>
                      <p className="blog-excerpt">{post.excerpt}</p>
                      <div className="blog-author">
                        <img src={post.author.avatar} alt={post.author.name} className="author-avatar" />
                        <span className="author-name">By {post.author.name}</span>
                      </div>
                    </div>
                  </>
                )}
                
                {post.type === 'news' && (
                  <div className="news-card-content">
                    <div className="news-meta">
                      <span className="news-badge">News</span>
                      <span className="news-date">{post.formattedDate}</span>
                    </div>
                    <h3 className="news-title">{post.title}</h3>
                    <p className="news-summary">{post.summary}</p>
                    <div className="news-read-time">
                      <Clock size={14} />
                      <span>{post.readTime} min read</span>
                    </div>
                  </div>
                )}
                
                <div className="blog-hover-overlay">
                  <ArrowRight size={24} />
                </div>
              </article>
            ))}
          </div>
          
          <div className="blog-cta">
            <button onClick={() => window.location.href = '/blog'} className="blog-view-all-btn">
              View All Posts
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Enhanced Pricing Section */}
      <section id="pricing" className={`pricing-section section-animate ${isVisible.pricing ? 'visible' : ''}`}>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Choose Your Learning Journey</h2>
            <p className="section-subtitle">Start free and upgrade as you grow</p>
          </div>
          
          <div className="pricing-cards-modern">
            {/* Free Plan */}
            <div className="pricing-card-modern">
              <div className="pricing-header-modern">
                <div className="plan-badge free">Free</div>
                <h3>Basic</h3>
                <div className="pricing-amount">
                  <span className="currency">$</span>
                  <span className="price">0</span>
                  <span className="period">/month</span>
                </div>
                <p className="plan-description">Perfect for getting started with AI-powered learning</p>
              </div>
              
              <div className="pricing-features-modern">
                <div className="feature-category">
                  <h4>Features</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Personalized Learning Roadmap</li>
                    <li><CheckCircle size={16} /> 1 Educational goal</li>
                    <li><CheckCircle size={16} /> 1 Topic per goal</li>
                    <li><CheckCircle size={16} /> Notes management</li>
                    <li><CheckCircle size={16} /> 5 AI Chat Queries/day</li>
                    <li><CheckCircle size={16} /> Pomodoro Timer</li>
                  </ul>
                </div>
              </div>
              
              <button 
                onClick={() => window.location.href = '/register?plan=free'} 
                className="pricing-cta-modern primary"
              >
                Get Started Free
                <ArrowRight size={18} />
              </button>
            </div>
            
            {/* Pro Plan */}
            <div className="pricing-card-modern featured">
              <div className="popular-badge">Most Popular</div>
              <div className="pricing-header-modern">
                <div className="plan-badge pro">Pro</div>
                <h3>Professional</h3>
                <div className="pricing-amount">
                  <span className="currency">£</span>
                  <span className="price">5</span>
                  <span className="period">/month</span>
                </div>
                <p className="plan-description">For serious learners who want unlimited access</p>
              </div>
              
              <div className="pricing-features-modern">
                <div className="feature-category">
                  <h4>Everything in Free, plus:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Up to 10 goals in progress</li>
                    <li><CheckCircle size={16} /> Unlimited AI Chat Queries</li>
                    <li><CheckCircle size={16} /> Unlimited AI Lessons</li>
                    <li><CheckCircle size={16} /> Unlimited AI Assessments</li>
                    <li><CheckCircle size={16} /> Best AI Models</li>
                  </ul>
                </div>
              </div>
              
              <button 
                onClick={handleUpgradeToPro}
                disabled={isUpgrading}
                className="pricing-cta-modern primary"
              >
                {isUpgrading ? (
                  <>
                    <Shield size={18} />
                    Processing...
                  </>
                ) : (
                  <>
                    Upgrade to Pro
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </div>
          </div>
          
          <div className="pricing-guarantee">
            <Shield size={20} />
            <span>30-day money-back guarantee • Cancel anytime • No hidden fees</span>
          </div>
        </div>
      </section>

      {/* Enhanced Footer */}
      <footer className="landing-footer-modern">
        <div className="footer-container-modern">
          <div className="footer-top">
            <div className="footer-brand">
              <div className="footer-logo">
                <span>Brainy</span>
              </div>
              <p className="footer-description">
                Empowering learners worldwide with AI-driven personalized education
              </p>
              <div className="footer-stats">
                <div className="stat">
                  <span className="stat-number">1K+</span>
                  <span className="stat-label">Active Learners</span>
                </div>
                <div className="stat">
                  <span className="stat-number">10K+</span>
                  <span className="stat-label">Lessons Generated</span>
                </div>
                <div className="stat">
                  <span className="stat-number">4.9</span>
                  <span className="stat-label">User Rating</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <div className="footer-copyright">
              <p>&copy; {new Date().getFullYear()} Brainy AI. All rights reserved.</p>
            </div>
            <div className="footer-social">
              <a href="/terms" className="social-link" target="_blank" rel="noopener noreferrer">Terms</a>
              <a href="/privacy" className="social-link" target="_blank" rel="noopener noreferrer">Privacy</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Cookie Banner */}
      <CookieBanner />
    </div>
  );
};

export default Landing; 